import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import { transaction } from '@cashew/types';
import { TFunction } from '@cashew/hooks';
import moment from 'moment';
import {
  BackArrow,
  Box,
  PageWithNavigation,
  Title,
  ListTransactions,
  Button,
  Modal,
} from '../../components';
import './index.css';

const { api, dateFormat, priceFormat } = require('@cashew/utils');

const ns = 'settlement-details';

const getSettlement = (
  settlementId: any,
  setSettlement: any,
  setLoading: any,
  setTransactionList: any,
  dispatch: any,
  t: TFunction
) => {
  setLoading(true);

  const negativeTransactionType = [
    'MERCHANT_ORDER_FULL_PAYMENT',
    'PAYMENT_GATEWAY_TRANSACTION_FEE',
    'MERCHANT_ORDER_SERVICE_FEE_FULL_REFUND_VAT',
    'MERCHANT_ORDER_SERVICE_FEE_FULL_REFUND',
  ];
  api
    .get({
      endpoint: `settlements/${settlementId}`,
    })
    .then((resp: any) => {
      setSettlement(resp.data);
      setTransactionList(
        resp.data.transactions.map((item: transaction.TransactionType) => {
          const newItem = item;
          newItem.totalAmount *= negativeTransactionType.includes(
            item.transactionType
          )
            ? -1
            : 1;
          return newItem;
        })
      );
    })
    .catch(() =>
      dispatch({
        type: 'OPEN_TOASTER',
        payload: {
          toasterTitle: t('settlementDetails.error.title.fetchingDetails'),
          toasterMessage: t('settlementDetails.error.message.fetchingDetails'),
          toasterType: 'error',
          toasterVisible: true,
          toasterDismiss: null,
        },
      })
    );
};

/**
 * Orders Page RefundPage
 */
export default () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const params: any = useParams();
  const [settlement, setSettlement] = useState<any | undefined>({});
  const [loading, setLoading] = useState(false);
  const [transactionList, setTransactionList] = useState([]);
  const [transactionSelected, setTransactionSelected] = useState(0);
  const [showProcessedConfirmationModal, setShowProcessedConfirmationModal] =
    useState(false);

  useEffect(() => {
    if (settlement === undefined || !loading) {
      getSettlement(
        params.settlementId,
        setSettlement,
        setLoading,
        setTransactionList,
        dispatch,
        t
      );
    }
  }, [settlement]);

  const showTransactionDetails = ({ transactionId }: any) => {
    if (transactionId === transactionSelected) {
      setTransactionSelected(0);
      return Promise.reject();
    }
    setTransactionSelected(transactionId);
    return api.get({
      endpoint: `transactions/${transactionId}`,
    });
  };

  const canBeProcessed =
    moment(settlement.cycleEndDate).format('YYYY-MM-DD') >=
    moment().format('YYYY-MM-DD');

  return (
    <div className={ns}>
      <Helmet>
        <title>{t('settlementDetails.title')}</title>
      </Helmet>
      <PageWithNavigation>
        <Title>
          {t('settlementDetails.title')}
          <BackArrow title={t('settlementDetails.button.backToSettlements')} />
        </Title>
        {settlement && (
          <Box withHeader className={`${ns}__box`}>
            <div className={`${ns}__line`}>
              <p>
                <span>{t('settlementDetails.settlementId')}</span>
                <strong>{settlement.settlementId}</strong>
              </p>
              <p>
                <span>{t('settlementDetails.settlementStatus')}</span>
                <strong>{t(`status.${settlement.status}`)}</strong>
              </p>
              <p className="flexdate">
                <span>{t('settlementDetails.settlementDates')}</span>
                <strong className="date">
                  {`${dateFormat({
                    date: settlement.cycleStartDate,
                    withTime: true,
                  })} - ${dateFormat({
                    date: settlement.cycleEndDate,
                    withTime: true,
                  })}`}
                </strong>
              </p>
              <p>
                <span>{t('settlementDetails.settlementAmount')}</span>
                <strong
                  className={cn(
                    {
                      [`${ns}--positive`]: settlement.balance * -1 > 0,
                    },
                    { [`${ns}--negative`]: settlement.balance * -1 < 0 }
                  )}
                >
                  {priceFormat({
                    currency: settlement.currencyCode,
                    amount: settlement.balance * -1,
                    isBalance: true,
                  })}
                </strong>
              </p>
            </div>
            <>
              {settlement.status === 'PENDING' && (
                <footer>
                  <div className={`${ns}__actions`}>
                    {settlement.status === 'PENDING' && (
                      <Button
                        disabled={canBeProcessed}
                        extraClass={cn(
                          {
                            [`${ns}__confirm`]: true,
                          },
                          { [`${ns}__disabled`]: canBeProcessed }
                        )}
                        onClick={() => {
                          setShowProcessedConfirmationModal(true);
                        }}
                      >
                        Mark as processed
                      </Button>
                    )}
                  </div>
                </footer>
              )}
            </>
          </Box>
        )}

        <ListTransactions
          ns={ns}
          transactionList={transactionList}
          showTransactionDetails={showTransactionDetails}
          transactionSelected={transactionSelected}
          t={t}
        />
      </PageWithNavigation>

      {showProcessedConfirmationModal && (
        <Modal
          className="processed-confirmation-modal"
          isVisible={showProcessedConfirmationModal}
          onClose={() => {
            setShowProcessedConfirmationModal(false);
          }}
        >
          <div className="modal-header">
            <h2>{t('settlementDetails.modal.title')}</h2>
          </div>

          <div className="modal-content">
            {t('settlementDetails.modal.content')}
          </div>

          <div className="modal-footer">
            <Button
              extraClass="btn-outline-black"
              onClick={() => {
                setShowProcessedConfirmationModal(false);
              }}
            >
              {t('settlementDetails.modal.action.no')}
            </Button>

            <Button
              onClick={() => {
                api
                  .patch({
                    endpoint: `settlements/${params.settlementId}/status`,
                  })
                  .then(() => {
                    dispatch({
                      type: 'OPEN_TOASTER',
                      payload: {
                        toasterTitle: 'Change settlement status',
                        toasterMessage:
                          'Your settlement was changed sucessfully',
                        toasterType: 'success',
                        toasterVisible: true,
                        toasterDismiss: () => {
                          window.location.reload();
                        },
                      },
                    });
                    settlement.status = 'PROCESSED';
                    setShowProcessedConfirmationModal(false);
                  });
              }}
            >
              {t('settlementDetails.modal.action.yes')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};
